// Talking to the server.

import { session, showSignIn } from "./session.ts";

/**
 * Read a response as JSON, but never let a parse failure hide the real
 * problem: an empty body from a proxy hiccup should say so.
 */
export async function readJson<T = any>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) {
    throw new Error(
      res.status >= 500 ? `The server is not answering (${res.status}). Try again in a moment.` : `Empty response (${res.status}).`
    );
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(`Unexpected reply (${res.status}): ${text.slice(0, 120)}`);
  }
}

declare global {
  interface Window {
    /** Requests the page started before the app's code arrived (index.html). */
    __early?: Record<string, Promise<Response>>;
  }
}

/** The answer to a GET the page already asked, taken once; the next ask of
 *  the same thing asks the server again. */
export function takeEarly(path: string): Promise<Response> | null {
  const early = window.__early;
  const got = early?.[path];
  if (!got) return null;
  delete early![path];
  return got;
}

/** Any API call can come back 401 once a session expires. */
export async function api(path: string, options?: RequestInit): Promise<Response> {
  const early = !options || !options.method || options.method === "GET" ? takeEarly(path) : null;
  const res = await (early || fetch(path, options));
  if (res.status === 401 && session.authRequired) {
    showSignIn();
    throw new Error("signed out");
  }
  return res;
}

export const postJson = (path: string, body: unknown): Promise<Response> =>
  api(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export const isSignedOut = (err: unknown): boolean => String(err instanceof Error ? err.message : err) === "signed out";

export class RequestError extends Error {
  readonly status: number;
  readonly data: any;
  constructor(message: string, status: number, data: any) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

/** A JSON request that throws the server's own words when it refuses. */
export async function sendJson<T = any>(method: string, path: string, body?: unknown, { signal }: { signal?: AbortSignal } = {}): Promise<T> {
  const res = await api(
    path,
    method === "GET" && body === undefined && !signal
      ? undefined
      : { method, signal, headers: body === undefined ? {} : { "content-type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) }
  );
  const data = await readJson(res);
  if (!res.ok) throw new RequestError(data.error || `The request failed (${res.status}).`, res.status, data);
  return data as T;
}

export const getJson = <T = any>(path: string): Promise<T> => sendJson<T>("GET", path);

export const errorText = (err: unknown): string => String(err instanceof Error ? err.message : err);

export const sleep = (ms: number): Promise<void> => new Promise((done) => setTimeout(done, ms));

// --- SAYING WHAT WENT WRONG ---
//
// Something went wrong in this page: a move the board was handed and could
// not play, a script that threw. It is sent to the server, which keeps it for
// the admin page — so the first person it happens to is the last, rather than
// the one who happens to mention it. Each thing is said once a page, and a
// page that keeps failing stops saying so after a while.

const faultsSaid = new Set<string>();

export function reportFault(kind: string, detail: string, context?: Record<string, unknown>): void {
  if (!session.email) return;
  const key = `${kind}|${detail}`;
  if (faultsSaid.has(key) || faultsSaid.size >= 30) return;
  faultsSaid.add(key);
  try {
    fetch("/api/faults", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        kind: String(kind).slice(0, 64),
        detail: String(detail).slice(0, 1000),
        place: location.pathname + location.search,
        context: Object.assign({ build: session.build, agent: navigator.userAgent }, context || {}),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Nothing to be done about a report that cannot be sent.
  }
}

window.addEventListener("error", (event) => {
  reportFault("script-error", `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`, { source: "app" });
});
window.addEventListener("unhandledrejection", (event) => {
  const reason = event.reason;
  // A request the server refused is handled where it was made; what is left
  // is a promise nobody was waiting on.
  if (isSignedOut(reason)) return;
  reportFault("script-error", String(reason && reason.stack ? reason.stack : reason).slice(0, 1000), { source: "app" });
});

// --- A NEWER BUILD ---
//
// A tab left open keeps the JavaScript it started with, so a deploy lands on
// the server and not on the person looking at it. Nothing is interrupted: the
// flag is acted on at the next navigation, when the screen is being replaced
// anyway.

export let staleBuild = false;
let buildCheckedAt = 0;

/** A newer build is on the server: the next navigation loads it. */
export function markStale(): void {
  staleBuild = true;
}

export function buildChecked(): void {
  buildCheckedAt = Date.now();
}

export async function checkBuild(): Promise<void> {
  if (staleBuild || !session.build) return;
  if (Date.now() - buildCheckedAt < 60_000) return;
  buildCheckedAt = Date.now();
  try {
    // Deliberately not `api()`: a session that has expired is the sign-in
    // gate's problem, not this check's.
    const res = await fetch("/api/me", { cache: "no-store" });
    if (!res.ok) return;
    const who = await res.json();
    if (who.build && who.build !== session.build) staleBuild = true;
  } catch {
    // Offline: look again later.
  }
}

document.addEventListener("visibilitychange", () => {
  if (!document.hidden) void checkBuild();
});
